import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../../app/Icon";
import "./account-menu.css";

export type AccountMenuAccount = Readonly<{
  email: string;
  name: string;
  paid: boolean;
  credits?: number;
  plan?: string | null;
  cancelAtPeriodEnd?: boolean;
  manageUrl?: string | null;
  trialRemaining: number;
}>;

type AccountView = "menu" | "profile" | "billing" | "settings";

type AccountMenuProps = Readonly<{
  account: AccountMenuAccount;
  theme: "dark" | "light";
  onToggleTheme: () => void;
  onSignOut: () => Promise<void>;
}>;

function initials(name: string, email: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? `${words[0]![0]}${words.at(-1)![0]}` : words[0]?.slice(0, 2) ?? email.slice(0, 2);
  return letters.toUpperCase();
}

function planLabel(account: AccountMenuAccount): string {
  if (!account.paid) return "Free";
  const plan = account.plan?.trim();
  return plan ? `${plan[0]!.toUpperCase()}${plan.slice(1)}` : "Paid";
}

function safeManageUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url.toString() : null;
  } catch {
    return null;
  }
}

function AccountAvatar({ account, large = false }: { account: AccountMenuAccount; large?: boolean }) {
  return <span className={`account-menu__avatar${large ? " account-menu__avatar--large" : ""}`} aria-hidden="true">
    {initials(account.name, account.email)}
  </span>;
}

export function AccountMenu({ account, theme, onToggleTheme, onSignOut }: AccountMenuProps) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<AccountView>("menu");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const plan = planLabel(account);
  const manageUrl = useMemo(() => safeManageUrl(account.manageUrl), [account.manageUrl]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setView("menu");
        setError(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      setView("menu");
      setError(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => {
      if (view === "menu") panelRef.current?.querySelector<HTMLElement>("[data-account-first]")?.focus();
      else panelRef.current?.querySelector<HTMLElement>("h2")?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [open, view]);

  function close() {
    setOpen(false);
    setView("menu");
    setError(false);
  }

  async function exit() {
    if (busy) return;
    setBusy(true);
    setError(false);
    try {
      await onSignOut();
      close();
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }

  const back = <button type="button" className="account-menu__back" aria-label="Back to account menu" onClick={() => setView("menu")}>
    <Icon name="arrow-left" />
  </button>;

  return <div className="account-menu" ref={rootRef}>
    <button
      ref={triggerRef}
      type="button"
      className="account-menu__trigger"
      aria-label={`${open ? "Close" : "Open"} account menu for ${account.name}`}
      aria-expanded={open}
      aria-controls={panelId}
      aria-haspopup="dialog"
      onClick={() => {
        if (open) close();
        else setOpen(true);
      }}
    >
      <AccountAvatar account={account} />
      <span className="account-menu__status"><span className="account-menu__sr-only">Signed in</span></span>
    </button>

    {open ? <div ref={panelRef} id={panelId} className="account-menu__panel" role="dialog" aria-label="Account menu">
      {view === "menu" ? <>
        <h2 className="account-menu__sr-only">Account menu</h2>
        <div className="account-menu__identity">
          <AccountAvatar account={account} large />
          <span className="account-menu__identity-copy">
            <strong title={account.name}>{account.name}</strong>
            <span title={account.email}>{account.email}</span>
            <small>{plan}</small>
          </span>
        </div>
        <nav className="account-menu__actions" aria-label="Account">
          <button data-account-first type="button" onClick={() => setView("profile")}><Icon name="user" /><span>View profile</span><Icon name="chevron-right" /></button>
          <button type="button" onClick={() => setView("billing")}><Icon name="credit-card" /><span>Billing</span><Icon name="chevron-right" /></button>
          <button type="button" onClick={() => setView("settings")}><Icon name="settings" /><span>Account settings</span><Icon name="chevron-right" /></button>
        </nav>
        <div className="account-menu__logout">
          <button type="button" disabled={busy} onClick={() => void exit()}><Icon name="logout" /><span>{busy ? "Signing out…" : error ? "Retry sign out" : "Log out"}</span></button>
          {error ? <p role="alert">Couldn’t sign out. Try again.</p> : null}
        </div>
      </> : view === "profile" ? <>
        <div className="account-menu__subheader">{back}<h2 tabIndex={-1}>Profile</h2></div>
        <div className="account-menu__profile">
          <AccountAvatar account={account} large />
          <strong>{account.name}</strong>
          <span>{account.email}</span>
        </div>
        <dl className="account-menu__details">
          <div><dt>Plan</dt><dd>{plan}</dd></div>
          <div><dt>{account.paid ? "Stencil credits" : "Free generations"}</dt><dd>{account.paid ? account.credits ?? 0 : account.trialRemaining}</dd></div>
        </dl>
      </> : view === "billing" ? <>
        <div className="account-menu__subheader">{back}<h2 tabIndex={-1}>Billing</h2></div>
        <div className="account-menu__section-copy">
          <span className="account-menu__plan">{plan} plan</span>
          <p>{account.paid ? `${account.credits ?? 0} stencil credits available.` : `${account.trialRemaining} free generations remaining.`}</p>
          {account.cancelAtPeriodEnd ? <p>Your subscription is set to end after the current billing period.</p> : null}
        </div>
        {manageUrl ? <a className="account-menu__primary" href={manageUrl} onClick={close}>Manage subscription<Icon name="arrow-right" /></a>
          : <Link className="account-menu__primary" to="/#pricing" onClick={close}>View plans<Icon name="arrow-right" /></Link>}
      </> : <>
        <div className="account-menu__subheader">{back}<h2 tabIndex={-1}>Account settings</h2></div>
        <div className="account-menu__setting">
          <span><strong>Appearance</strong><small>{theme === "dark" ? "Dark" : "Light"} theme</small></span>
          <button type="button" onClick={onToggleTheme}><Icon name={theme === "dark" ? "sun" : "moon"} />Use {theme === "dark" ? "light" : "dark"}</button>
        </div>
        <div className="account-menu__setting">
          <span><strong>Password</strong><small>Send a secure reset link</small></span>
          <Link to="/forgot-password" state={{ email: account.email }} onClick={close}>Reset password</Link>
        </div>
      </>}
    </div> : null}
  </div>;
}
